import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { User, Bot, ArrowDown, ArrowUp, Copy, Check, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, FileText, X, MoreHorizontal } from 'lucide-react';
import { Message } from '../../types';
import { cleanMessageContent } from '../../utils/messageParser';
import { parseContentWithJson, renderParsedContent } from '../../utils/jsonContentParser';
import { ChatInput } from './ChatInput';
import { StyledIonoff, StyledIonoffBlocks } from '../ui/StyledIonoff';
import { MultiColorIonoff } from '../ui/MultiColorIonoff';
import { JsonApiDisplay } from './JsonApiDisplay';
import { InlineJsonDisplay } from './InlineJsonDisplay';
import { CompactMessage } from './CompactMessage';
import { MarkdownRenderer } from './MarkdownRenderer';
import { ModelChip } from './ModelChip';
import { HighlightedMarkdownRenderer } from './HighlightedMarkdownRenderer';
import { UserMessageDetails } from './UserMessageDetails';
import { DebugInfoMessage } from './DebugInfoMessage';
import { StarRatingControl } from './StarRatingControl';
import { ResumeButton } from './ResumeButton';
import { EditPromptButton } from './EditPromptButton';
import { useHighlighting } from '../../hooks/useHighlighting';
import { useAuth } from '../../hooks/useAuth';
import { ConversationGradeState, RawGrade } from '../../services/localGradeProcessor';
import { compareMajorityJudgments, MajorityJudgment, GradeCounts } from '../../services/majorityJudgmentService';
import { majorityGrade } from '../../services/majorityJudgment';
import {
  buildSynthesisWordHighlightMap,
  buildCandidateWordHighlightMap,
} from '../../utils/wordHighlightBuilder';
import { useWelcomes } from '../../hooks/useWelcomes';
import { usePromptBoxInstructions } from '../../hooks/usePromptBoxInstructions';
import { MessageLogService } from '../../services/messageLogService';

interface ChatWindowProps {
  messages: Message[];
  onSendMessage: (message: string, plusHumanContent?: string) => void;
  disabled?: boolean;
  highlighting: ReturnType<typeof useHighlighting>;
  modelColorAssignments?: Map<string, string>; // Optional fixed color assignments
  conversationId?: string | null; // Current conversation ID
  // Toggle states
  webEnabled: boolean;
  setWebEnabled: (value: boolean) => void;
  enterSendsEnabled: boolean;
  setEnterSendsEnabled: (value: boolean) => void;
  selfEvalEnabled: boolean;
  setSelfEvalEnabled: (value: boolean) => void;
  txtEnabled: boolean;
  setTxtEnabled: (value: boolean) => void;
  plusHumanEnabled: boolean;
  setPlusHumanEnabled: (value: boolean) => void;
  humanLeaderboardEnabled: boolean;
  setHumanLeaderboardEnabled: (value: boolean) => void;
  // Tools expanded state (controls visibility of other toggle buttons)
  toolsExpanded: boolean;
  setToolsExpanded: (value: boolean) => void;
  // Target count for conditional button visibility
  targetCount?: number;
  // Priority setting for model tagging
  prioritySetting?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom';
  // Clean view mode - only show AI responses and final ionoff response
  cleanViewEnabled?: boolean;
  // Highlighted message ID for navigation flash effect
  highlightedMessageId?: string | null;
  // +Human feature functions and state
  assignStarGrade?: (messageId: string, grade: 'A' | 'B' | 'C' | 'D' | 'F', candidateModelId: string, isSelfEval: boolean) => Promise<void>;
  deleteStarGrade?: (messageId: string) => Promise<void>; // NEW: Delete star rating
  resumeFromPause?: () => Promise<void>;
  canResume?: boolean;
  isResuming?: boolean; // NEW: Prevent duplicate resume calls
  txtPaused?: boolean;
  txtPausePhase?: 'awaiting-model-grades' | 'awaiting-ionoff-grade' | null;
  txtModelGradesReceived?: number;
  txtIonoffGradeReceived?: number;
  txtResumeMessageId?: string | null;
  ionoffArrived?: boolean;
  // Scroll container ref for response dots tracking
  scrollContainerRefCallback?: (ref: HTMLDivElement | null) => void;
  // Admin feature
  showAdminInput?: boolean;
  setShowAdminInput?: (value: boolean) => void;
  adminConversationCount?: string;
  setAdminConversationCount?: (value: string) => void;
  onLoadAdminConversations?: (count: number) => void;
  isLoadingAdminConversations?: boolean;
  // Model display mode (creator vs full name)
  showModelNames?: boolean;
  // Toggle model display mode (creator vs full name)
  onToggleModelNames?: () => void;
  // 🎯 PHASE 2: Edit prompt functionality
  onEditPrompt?: (messageId: string, content: string) => void;
  // Production mode - hides dev-only UI elements
  prodMode?: boolean;
  // Local grade state for computing peer review champion
  localGradeState?: ConversationGradeState | null;
  // Tag colors for ionoff logo (from active AI models)
  tagColors?: string[];
  // Dismissed icebreakers (exact text matches)
  dismissedIcebreakers?: string[];
  // Callback when user dismisses an icebreaker
  onDismissIcebreaker?: (icebreaker: string) => void;
  // Bottom section collapse state
  isBottomSectionCollapsed?: boolean;
  // Thinking/reasoning effort control
  reasoningEffort?: 'default' | 'low' | 'medium' | 'high';
  setReasoningEffort?: (value: 'default' | 'low' | 'medium' | 'high') => void;
  // Custom review rubric (for postscript)
  activeRubric?: {
    name: string;
    descriptions: {
      A: string;
      B: string;
      C: string;
      D: string;
      F: string;
    };
  } | null;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  messages, 
  onSendMessage, 
  disabled = false, 
  highlighting,
  modelColorAssignments,
  conversationId,
  webEnabled,
  setWebEnabled,
  enterSendsEnabled,
  setEnterSendsEnabled,
  selfEvalEnabled,
  setSelfEvalEnabled,
  txtEnabled,
  setTxtEnabled,
  plusHumanEnabled,
  setPlusHumanEnabled,
  humanLeaderboardEnabled,
  showModelNames = false,
  onToggleModelNames,
  setHumanLeaderboardEnabled,
  toolsExpanded,
  setToolsExpanded,
  targetCount = 3,
  prioritySetting,
  cleanViewEnabled = true,
  highlightedMessageId = null,
  assignStarGrade,
  deleteStarGrade,
  resumeFromPause,
  canResume,
  isResuming = false,
  txtPaused,
  txtPausePhase,
  txtModelGradesReceived,
  txtIonoffGradeReceived,
  txtResumeMessageId,
  ionoffArrived = false,
  scrollContainerRefCallback,
  showAdminInput,
  setShowAdminInput,
  adminConversationCount,
  setAdminConversationCount,
  onLoadAdminConversations,
  isLoadingAdminConversations,
  onEditPrompt,
  prodMode = false,
  localGradeState,
  tagColors = [],
  dismissedIcebreakers = [],
  onDismissIcebreaker,
  isBottomSectionCollapsed = false,
  reasoningEffort = 'default',
  setReasoningEffort,
  activeRubric = null,
}) => {
  // Fetch welcomes and icebreakers from Firebase
  const { 
    welcomes,
    icebreakers,
    loading: welcomesLoading,
    currentMessage,
    currentListLength,
    displayMode,
    currentIndex,
    switchToIcebreakers,
    toggleHelpMeStart,
    goToPrevious,
    goToNext,
    canGoPrevious,
    canGoNext,
    dismissCurrentIcebreaker,
    reset: resetWelcomes,
    switchToSecondWelcome,
    markFirstVisitComplete
  } = useWelcomes({
    dismissedIcebreakers,
    onDismissIcebreaker
  });
  
  // Fetch prompt box instructions from Firebase
  const { instructions } = usePromptBoxInstructions();
  
  // Get user from auth hook
  const { user } = useAuth();
  
  // Track which messages should show raw markup (messageId -> boolean)
  const [showRawMarkup, setShowRawMarkup] = useState<Set<string>>(new Set());
  // Track if user has typed in the input (to hide welcome suffix)
  const [hasInputText, setHasInputText] = useState(false);
  // Track which messages have been copied (messageId -> boolean)
  const [copiedMessages, setCopiedMessages] = useState<Set<string>>(new Set());
  // Track previous messages length to detect new conversation starts
  const prevMessagesLengthRef = React.useRef<number>(0);
  // Track which messages have been logged (messageId -> boolean)
  const [loggedMessages, setLoggedMessages] = useState<Set<string>>(new Set());
  // Track which messages have expanded API dropdowns (messageId -> boolean)
  const [expandedApiDropdowns, setExpandedApiDropdowns] = useState<Set<string>>(new Set());
  // Track which user prompts have expanded metadata (tokens, size, sprise, etc.)
  const [expandedPromptMeta, setExpandedPromptMeta] = useState<Set<string>>(new Set());
  
  // Track which AI responses have been toggled from their default state
  // First response: default expanded; others: default collapsed
  // This set tracks messages where user clicked to toggle from default
  const [collapsedResponses, setCollapsedResponses] = useState<Set<string>>(() => {
    // Load from localStorage on init
    try {
      const saved = localStorage.getItem('ionoff-collapsed-responses');
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load collapsed responses from localStorage:', e);
    }
    return new Set();
  });
  
  // Persist collapsed responses to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('ionoff-collapsed-responses', JSON.stringify(Array.from(collapsedResponses)));
    } catch (e) {
      console.error('Failed to save collapsed responses to localStorage:', e);
    }
  }, [collapsedResponses]);
  
  // Toggle collapse state for a message
  const toggleResponseCollapse = useCallback((messageId: string) => {
    setCollapsedResponses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);
  
  // Ref for scroll container (for response dots viewport tracking)
  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (scrollContainerRefCallback) {
      scrollContainerRefCallback(node);
    }
  }, [scrollContainerRefCallback]);
  
  // Log messages as they are displayed to the user
  useEffect(() => {
    messages.forEach(message => {
      // Only log messages that haven't been logged yet
      if (!loggedMessages.has(message.id)) {
        MessageLogService.logMessage(message);
        setLoggedMessages(prev => new Set(prev).add(message.id));
      }
    });
  }, [messages, loggedMessages]);
  
  // Reset welcomes when starting a new conversation (messages.length changes from > 0 to 0)
  // Switch to second welcome when first message is sent (messages.length goes from 0 to > 0)
  useEffect(() => {
    const prevLength = prevMessagesLengthRef.current;
    const currentLength = messages.length;
    
    // Only reset when transitioning from having messages to no messages (new conversation)
    if (prevLength > 0 && currentLength === 0) {
      resetWelcomes();
    }
    
    // Switch to second welcome (without "welcome to") after first message is sent
    if (prevLength === 0 && currentLength > 0) {
      switchToSecondWelcome();
    }
    
    // Update ref for next render
    prevMessagesLengthRef.current = currentLength;
  }, [messages.length, resetWelcomes, switchToSecondWelcome]);
  
  // Find synthesis and candidate messages for highlighting
  const { synthesisMessage, candidateMessages } = useMemo(() => {
    const synthesis = messages.find(m => m.isSynthesized);
    const candidates = messages.filter(
      m => m.role === 'assistant' &&
           !m.isSynthesized &&
           !m.isCostSummary &&
           !m.isGradeMatrix &&
           !m.isGradingResponse &&
           !m.isExtractionResponse &&
           m.modelUsed !== 'ionoff-debug' &&
           m.modelUsed !== 'ionoff-system' &&
           m.content &&
           m.content.length > 50 // Substantive responses only
    );
    console.log('📊 Highlighting setup:', {
      totalMessages: messages.length,
      hasSynthesis: !!synthesis,
      synthesisId: synthesis?.id,
      candidateCount: candidates.length,
      candidateModels: candidates.map(c => c.modelUsed),
      candidateIds: candidates.map(c => c.id)
    });
    return { synthesisMessage: synthesis, candidateMessages: candidates };
  }, [messages]);

  // 🎯 FIX: Reorder messages so responses appear under their triggering prompt
  // Uses userPromptMessageId for proper grouping instead of global timestamp order
  const orderedMessages = useMemo(() => {
    // 🎯 PHASE 2: Filter out overwritten messages (edited prompts and their responses)
    const activeMessages = messages.filter(m => !m.overwritten);
    
    // Collect user messages in order
    const userMessages = activeMessages.filter(m => m.role === 'user');
    
    // Create groups: each user message + its responses
    const groups: Array<typeof messages> = [];
    
    for (const userMsg of userMessages) {
      const group: typeof activeMessages = [userMsg];
      
      // Find all responses for this prompt
      for (const msg of activeMessages) {
        if (msg.role === 'user') continue;
        
        // Use userPromptMessageId if available
        const targetPromptId = (msg as any).userPromptMessageId;
        
        if (targetPromptId === userMsg.id) {
          group.push(msg);
        } else if (!targetPromptId) {
          // Legacy fallback: check if this response came after this user msg
          // but before the next user msg
          const userMsgIndex = userMessages.indexOf(userMsg);
          const nextUserMsg = userMessages[userMsgIndex + 1];
          
          if (msg.timestamp >= userMsg.timestamp && 
              (!nextUserMsg || msg.timestamp < nextUserMsg.timestamp)) {
            group.push(msg);
          }
        }
      }
      
      // Sort responses within group by timestamp
      const responses = group.slice(1).sort((a, b) => 
        a.timestamp.getTime() - b.timestamp.getTime()
      );
      groups.push([group[0], ...responses]);
    }
    
    // Flatten groups back to single array
    return groups.flat();
  }, [messages]);

  const formatTimestamp = (date: Date): string => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  /**
   * Compute word-level highlight map for synthesis message
   * Shows which words appear in active models' responses
   * Uses additive color blending when multiple models match the same word
   */
  const computeSynthesisHighlighting = useMemo(() => {
    console.log('🎨 computeSynthesisHighlighting:', {
      hasSynthesisMessage: !!synthesisMessage,
      synthesisMessageId: synthesisMessage?.id,
      activeHighlightsSize: highlighting.activeHighlights.size,
      activeHighlightsKeys: Array.from(highlighting.activeHighlights.keys())
    });
    
    if (!synthesisMessage || highlighting.activeHighlights.size === 0) {
      console.log('🎨 computeSynthesisHighlighting: early return (no synthesis or no active highlights)');
      return null;
    }
    
    // Build array of candidate texts with their colors (excluding ionoff)
    const candidateTextsWithColors: Array<{ text: string; color: string }> = [];
    
    highlighting.activeHighlights.forEach((highlight, modelId) => {
      // Skip ionoff - it's not a candidate model
      if (modelId === 'ionoff') return;
      
      // Try exact match first, then partial match (for AA display name vs OpenRouter ID mismatch)
      let candidate = candidateMessages.find(m => m.modelUsed === modelId);
      
      // If no exact match, try partial match (modelUsed might be AA display name while modelId is OpenRouter ID)
      if (!candidate) {
        // Extract the base model name from OpenRouter ID (e.g., "google/gemini-2.5-flash-lite-preview-09-2025" -> "gemini-2.5-flash-lite")
        const modelIdLower = modelId.toLowerCase();
        candidate = candidateMessages.find(m => {
          if (!m.modelUsed) return false;
          const modelUsedLower = m.modelUsed.toLowerCase();
          // Check if modelUsed contains the key parts of the modelId
          // Extract key identifiers from both
          const modelIdParts = modelIdLower.split(/[\/\-_\s]+/).filter(p => p.length > 2);
          const matchCount = modelIdParts.filter(part => modelUsedLower.includes(part)).length;
          return matchCount >= 3; // Require at least 3 parts to match
        });
      }
      
      console.log('🎨 Looking for candidate:', {
        modelId,
        found: !!candidate,
        foundModelUsed: candidate?.modelUsed,
        candidateModels: candidateMessages.map(m => m.modelUsed)
      });
      if (!candidate) return;

      const cleanedCandidate = cleanMessageContent(candidate.content, candidate.modelUsed);
      candidateTextsWithColors.push({
        text: cleanedCandidate,
        color: highlight.color,
      });
    });

    console.log('🎨 candidateTextsWithColors:', candidateTextsWithColors.length);
    if (candidateTextsWithColors.length === 0) return null;
    
    return buildSynthesisWordHighlightMap(candidateTextsWithColors);
  }, [synthesisMessage, candidateMessages, highlighting.activeHighlights]);

  /**
   * Compute word-level highlight map for a candidate message
   * Shows which words in the candidate also appear in ionoff synthesis
   */
  const computeCandidateHighlighting = useMemo(() => {
    return (candidateMessage: Message) => {
      // Only highlight if ionoff is explicitly activated
      if (!synthesisMessage || !highlighting.isIonoffActive || !highlighting.ionoffColor) {
        return null;
      }

      const cleanedSynthesis = cleanMessageContent(synthesisMessage.content, synthesisMessage.modelUsed);

      return buildCandidateWordHighlightMap(
        cleanedSynthesis,
        highlighting.ionoffColor
      );
    };
  }, [synthesisMessage, highlighting.isIonoffActive, highlighting.ionoffColor]);

  const toggleMarkupView = (messageId: string) => {
    setShowRawMarkup(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleCopyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessages(prev => new Set(prev).add(messageId));
      setTimeout(() => {
        setCopiedMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  const handleLoadLog = async (messageId: string) => {
    try {
      const logContent = MessageLogService.getLogBetweenPromptAndMessage(
        messages, 
        messageId,
        expandedApiDropdowns
      );
      if (logContent) {
        await navigator.clipboard.writeText(logContent);
        // Show brief feedback using a different mechanism to avoid confusion with copy button
        // We'll use a temporary visual indicator
        console.log('Log loaded to clipboard:', logContent.split('\n').length, 'lines');
      } else {
        console.warn('No log content found between user prompt and this message');
      }
    } catch (err) {
      console.error('Failed to load log:', err);
      alert('Failed to load log to clipboard');
    }
  };

  // Handle toggling API dropdown expansion
  const handleApiDropdownToggle = (messageId: string, isOpen: boolean) => {
    setExpandedApiDropdowns(prev => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(messageId);
      } else {
        newSet.delete(messageId);
      }
      return newSet;
    });
  };

  // Check if a message has a previous user message (for showing log button)
  const hasPreviousUserMessage = (messageIndex: number): boolean => {
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return true;
      }
    }
    return false;
  };

  // Determine if message should use compact format
  const shouldBeCompact = (message: Message): boolean => {
    // Never compact user messages
    if (message.role === 'user') return false;
    
    // Never compact synthesis, cost summary, grade matrix, or grading responses
    if (message.isSynthesized || message.isCostSummary || message.isGradeMatrix || message.isGradingResponse) return false;
    
    // Never compact error messages - they should be prominently displayed
    if (message.modelUsed === 'ionoff-error') return false;
    
    // Only compact intermediate messages: debug API calls and extraction responses
    if (message.modelUsed === 'ionoff-debug') return true;
    if (message.isExtractionResponse) return true;
    
    // Regular model responses should NOT be compact
    return false;
  };

  // Determine if message should have collapse/expand control
  // This applies to regular AI responses (not synthesis, not user, not system messages)
  const isCollapsibleResponse = (message: Message): boolean => {
    // Only assistant messages
    if (message.role !== 'assistant') return false;
    
    // Not synthesis messages (they have the special ionoff header)
    if (message.isSynthesized) return false;
    
    // Not system/utility messages
    if (message.isCostSummary || message.isGradeMatrix || message.isGradingResponse) return false;
    if (message.isExtractionResponse || message.isDebugInfo) return false;
    if (message.modelUsed === 'ionoff-debug' || message.modelUsed === 'ionoff-error' || message.modelUsed === 'ionoff-system') return false;
    
    // Not human responses
    if (message.isHumanResponse) return false;
    
    // Must have a modelUsed (actual AI response)
    if (!message.modelUsed) return false;
    
    return true;
  };

  // Determine if a message is the first collapsible response for its prompt
  // Used to determine default collapse state (first = expanded, others = collapsed)
  const isFirstResponseForPrompt = useCallback((message: Message, messageIndex: number): boolean => {
    if (!isCollapsibleResponse(message)) return false;
    
    // Find the preceding user message
    let userPromptIndex = -1;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user' && !messages[i].overwritten) {
        userPromptIndex = i;
        break;
      }
    }
    
    if (userPromptIndex === -1) return true; // No user prompt found, treat as first
    
    // Check if there's any collapsible response between user prompt and this message
    for (let i = userPromptIndex + 1; i < messageIndex; i++) {
      if (isCollapsibleResponse(messages[i])) {
        return false; // There's an earlier collapsible response
      }
    }
    
    return true; // This is the first collapsible response after the user prompt
  }, [messages]);

  // Get title for compact messages
  const getCompactTitle = (message: Message): string => {
    if (message.modelUsed === 'ionoff-debug') {
      const lines = message.content.split('\n');
      return lines[0] || 'API Call';
    }
    if (message.isExtractionResponse) {
      return `${message.modelUsed} (Extraction)`;
    }
    return message.modelUsed || 'AI Response';
  };

  // Get subtitle for compact messages
  const getCompactSubtitle = (message: Message): string | undefined => {
    if (message.isExtractionResponse && message.extractedFrom !== undefined) {
      return `→ Extracted grades from Model #${message.extractedFrom}${message.extractedFromModelId ? `=${message.extractedFromModelId}` : ''}`;
    }
    return undefined;
  };

  // Render message content for compact messages (inline, no nested dropdowns)
  const isResponseMessageType = (message: Message): boolean => {
    // Ionoff and human responses always high contrast
    if (message.isHumanResponse) return true;
    if (message.isSynthesized && !message.isCostSummary && !message.isGradeMatrix) return true;
    
    const isSelfEvalCall = (message as { isSelfEvalCall?: boolean }).isSelfEvalCall ?? false;
    if (message.role === 'assistant' && message.modelUsed) {
      if (
        message.isGradingResponse ||
        message.isCostSummary ||
        message.isGradeMatrix ||
        message.isDebugInfo ||
        message.isExtractionResponse ||
        isSelfEvalCall ||
        message.modelUsed === 'ionoff-debug'
      ) {
        return false;
      }
      // Non-ionoff AI responses: high contrast before ionoff arrives, lower after
      if (!message.synthesisVariant) {
        return !ionoffArrived; // High contrast only before ionoff arrives
      }
    }
    return false;
  };

  const renderCompactMessageContent = (message: Message, isRawView: boolean) => {
    const isResponseMessage = isResponseMessageType(message);
    const textColorClass = isResponseMessage ? 'text-foreground' : 'text-foreground/50'; // Lower contrast for non-response

    // If raw view is enabled, show the original content
    if (isRawView) {
      return (
        <div className={`${textColorClass} whitespace-pre-wrap leading-relaxed text-sm font-mono bg-muted/30 p-3 rounded border border-border`}>
          {message.content}
        </div>
      );
    }

    if (message.modelUsed === 'ionoff-debug') {
      // For debug messages, extract and render JSON inline
      const jsonMatch = message.content.match(/```json\n([\s\S]*?)\n```/);
      if (jsonMatch && jsonMatch[1]) {
        try {
          const jsonData = JSON.parse(jsonMatch[1]);
          return <InlineJsonDisplay data={jsonData} />;
        } catch (e) {
          const cleanedContent = cleanMessageContent(message.content, message.modelUsed);
          return <MarkdownRenderer content={cleanedContent} className={`text-sm ${textColorClass}`} />;
        }
      }
    }

    // For other messages, check if there's JSON in content or debug info
    if (message.debug?.openrouterRequest) {
      return <InlineJsonDisplay data={message.debug.openrouterRequest} />;
    }

    // Try to parse JSON from content
    const cleanedContent = cleanMessageContent(message.content, message.modelUsed);
    const segments = parseContentWithJson(cleanedContent);
    const jsonSegment = segments.find(seg => seg.type === 'json');
    
    if (jsonSegment?.jsonData) {
      return <InlineJsonDisplay data={jsonSegment.jsonData} />;
    }
    
    // Fallback to markdown rendered text
    return <MarkdownRenderer content={cleanedContent} className={`text-sm ${textColorClass}`} />;
  };
  // Render message content for full messages (with expandable modals)
  const renderFullMessageContent = (message: Message, isRawView: boolean) => {
    // 🎯 TWO-PHASE IONOFF: Show loading state while synthesis is in progress
    if (message.isSynthesized && message.synthesizing) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground text-sm animate-pulse">
          <span className="inline-block w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          <span>synthesizing response...</span>
        </div>
      );
    }

    const cleanedContent = cleanMessageContent(message.content, message.modelUsed);
    const isUserMessage = message.role === 'user';
    // User prompt bubble has OPPOSITE contrast from theme to stand out
    const userPromptBubbleClasses = 'inline-block max-w-[85%] px-3 py-2 rounded-2xl text-left bg-black text-white dark:bg-white dark:text-black ml-8';
    const isResponseMessage = isResponseMessageType(message);
    const messageTextClass = isResponseMessage ? 'text-foreground' : 'text-foreground/50'; // Responses get normal contrast, non-responses get lower contrast
    const baseContentClass = isUserMessage ? userPromptBubbleClasses : messageTextClass;
    
    // If raw view is enabled, show the original content
    if (isRawView) {
      return (
        <>
          <div className={`${
            isUserMessage
              ? userPromptBubbleClasses
              : `${messageTextClass} bg-muted/30 p-3 rounded border border-border`
          } whitespace-pre-wrap leading-relaxed mb-3 font-mono text-sm`}>
            {message.content}
          </div>
          
          {/* API Request Body - show for non-summary assistant messages with debug info - hidden in prodMode */}
          {!prodMode && message.role === 'assistant' && !message.isCostSummary && !message.isGradeMatrix && message.debug?.openrouterRequest && (
            <div className="flex justify-end w-full">
              <div className="max-w-[85%]">
                <JsonApiDisplay 
                  data={message.debug.openrouterRequest}
                  title={message.isExtractionResponse 
                    ? 'API Call Sent to Google Gemini API'
                    : `API Call Sent to ${message.debug.provider || 'OpenRouter'}`}
                  onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
                />
              </div>
            </div>
          )}
          
          {/* API Response - show grading response content - hidden in prodMode */}
          {!prodMode && message.role === 'assistant' && message.debug?.openrouterResponse && (
            <div className="flex justify-end w-full">
              <div className="max-w-[85%]">
                <JsonApiDisplay 
                  data={message.debug.openrouterResponse}
                  title={`API Response Received${message.debug.elapsedSeconds ? ` (${message.debug.elapsedSeconds}s)` : ''}${message.debug.costUSD ? ` ($${message.debug.costUSD.toFixed(6)})` : ''}`}
                  onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
                />
              </div>
            </div>
          )}
          
          {/* Synthesis API Call - show for synthesis messages - hidden in prodMode */}
          {!prodMode && message.role === 'assistant' && message.isSynthesized && message.debug?.synthesisApiCall && (
            <div className="flex justify-end w-full">
              <div className="max-w-[85%]">
                <JsonApiDisplay 
                  data={message.debug.synthesisApiCall}
                  title={`API Call Sent to ${message.debug.synthesisApiCall.provider || 'OpenRouter'}`}
                  onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
                />
              </div>
            </div>
          )}
          
          {/* User Message Send Configuration - show for user messages with sendConfig */}
          {message.role === 'user' && message.sendConfig && (
            <div className="flex justify-end w-full">
              <div className="max-w-[85%]">
                <UserMessageDetails sendConfig={message.sendConfig} prodMode={prodMode} />
              </div>
            </div>
          )}
        </>
      );
    }
    
    // Check if this message should show highlighting
    const isCandidate = candidateMessages.some(c => c.id === message.id);
    const isCandidateActive = message.modelUsed && highlighting.isModelActive(message.modelUsed);
    
    const shouldHighlight = 
      (message.isSynthesized && computeSynthesisHighlighting) ||
      (isCandidate && isCandidateActive && highlighting.isIonoffActive);
    
    // Apply highlighting if applicable
    if (shouldHighlight && !message.isCostSummary && !message.isGradeMatrix) {
      const wordHighlightMap = message.isSynthesized
        ? computeSynthesisHighlighting
        : computeCandidateHighlighting(message);
      
      if (wordHighlightMap) {
        return (
          <>
            <div className={`${
              isUserMessage ? userPromptBubbleClasses : messageTextClass
            } leading-relaxed mb-3`}>
              <HighlightedMarkdownRenderer
                content={cleanedContent}
                wordHighlights={wordHighlightMap}
                isSynthesis={message.isSynthesized}
              />
            </div>
            
            {/* API Request Body - hidden in prodMode */}
            {!prodMode && message.role === 'assistant' && message.debug?.openrouterRequest && (
              <div className="flex justify-end w-full">
                <div className="max-w-[85%]">
                  <JsonApiDisplay 
                    data={message.debug.openrouterRequest}
                    title={message.isExtractionResponse 
                      ? 'API Call Sent to Google Gemini API'
                      : `API Call Sent to ${message.debug.provider || 'OpenRouter'}`}
                    onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
                  />
                </div>
              </div>
            )}
            
            {/* API Response - show grading response content - hidden in prodMode */}
            {!prodMode && message.role === 'assistant' && message.debug?.openrouterResponse && (
              <div className="flex justify-end w-full">
                <div className="max-w-[85%]">
                  <JsonApiDisplay 
                    data={message.debug.openrouterResponse}
                    title={`API Response Received${message.debug.elapsedSeconds ? ` (${message.debug.elapsedSeconds}s)` : ''}${message.debug.costUSD ? ` ($${message.debug.costUSD.toFixed(6)})` : ''}`}
                    onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
                  />
                </div>
              </div>
            )}
            
            {/* Synthesis API Call - show for synthesis messages - hidden in prodMode */}
            {!prodMode && message.role === 'assistant' && message.isSynthesized && message.debug?.synthesisApiCall && (
              <div className="flex justify-end w-full">
                <div className="max-w-[85%]">
                  <JsonApiDisplay 
                    data={message.debug.synthesisApiCall}
                    title={`API Call Sent to ${message.debug.synthesisApiCall.provider || 'OpenRouter'}`}
                    onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
                  />
                </div>
              </div>
            )}
            
            {/* User Message Send Configuration - show for user messages with sendConfig */}
            {message.role === 'user' && message.sendConfig && (
              <div className="flex justify-end w-full">
                <div className="max-w-[85%]">
                  <UserMessageDetails sendConfig={message.sendConfig} prodMode={prodMode} />
                </div>
              </div>
            )}
          </>
        );
      }
    }
    
    // Regular rendered view - parse and render with JSON detection
    const segments = parseContentWithJson(cleanedContent);
    const hasJsonSegments = segments.some(seg => seg.type === 'json');
    
    // Debug: Log grade matrix rendering
    if (message.isGradeMatrix) {
      console.log('🎨 renderFullMessageContent for grade matrix:', {
        messageId: message.id,
        cleanedContentLength: cleanedContent.length,
        cleanedContentPreview: cleanedContent.substring(0, 100),
        hasJsonSegments,
        isUserMessage
      });
    }
    
    return (
      <>
        {hasJsonSegments ? (
          <div className={`${
            isUserMessage ? userPromptBubbleClasses : messageTextClass
          } leading-relaxed mb-3 ${
            message.isCostSummary || message.isGradeMatrix ? 'font-mono text-sm' : ''
          }`}>
            {renderParsedContent(segments)}
          </div>
        ) : (
          // Use markdown renderer for regular content
          <div className={`mb-3 ${
            message.isCostSummary || message.isGradeMatrix ? 'font-mono text-sm whitespace-pre-wrap' : ''
          } ${
            isUserMessage ? userPromptBubbleClasses : messageTextClass
          }`}>
            {message.isCostSummary || message.isGradeMatrix ? (
              <div className="whitespace-pre-wrap">{cleanedContent}</div>
            ) : (
              <MarkdownRenderer 
                content={cleanedContent} 
                className={isUserMessage ? 'text-inherit' : (isResponseMessage ? undefined : 'text-muted-foreground')} 
              />
            )}
          </div>
        )}
        
        {/* API Request Body - show for non-summary assistant messages with debug info - hidden in prodMode */}
        {!prodMode && message.role === 'assistant' && !message.isCostSummary && !message.isGradeMatrix && message.debug?.openrouterRequest && (
          <div className="flex justify-end w-full">
            <div className="max-w-[85%]">
              <JsonApiDisplay 
                data={message.debug.openrouterRequest}
                title={message.isExtractionResponse 
                  ? 'API Call Sent to Google Gemini API'
                  : `API Call Sent to ${message.debug.provider || 'OpenRouter'}`}
                onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
              />
            </div>
          </div>
        )}
        
        {/* API Response - show grading response content - hidden in prodMode */}
        {!prodMode && message.role === 'assistant' && message.debug?.openrouterResponse && (
          <div className="flex justify-end w-full">
            <div className="max-w-[85%]">
              <JsonApiDisplay 
                data={message.debug.openrouterResponse}
                title={`API Response Received${message.debug.elapsedSeconds ? ` (${message.debug.elapsedSeconds}s)` : ''}${message.debug.costUSD ? ` ($${message.debug.costUSD.toFixed(6)})` : ''}`}
                onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
              />
            </div>
          </div>
        )}
        
        {/* Synthesis API Call - show for synthesis messages - hidden in prodMode */}
        {!prodMode && message.role === 'assistant' && message.isSynthesized && message.debug?.synthesisApiCall && (
          <div className="flex justify-end w-full">
            <div className="max-w-[85%]">
              <JsonApiDisplay 
                data={message.debug.synthesisApiCall}
                title={`API Call Sent to ${message.debug.synthesisApiCall.provider || 'OpenRouter'}`}
                onToggle={(isOpen) => handleApiDropdownToggle(message.id, isOpen)}
              />
            </div>
          </div>
        )}
        
        {/* User Message Send Configuration - show for user messages with sendConfig */}
        {message.role === 'user' && message.sendConfig && (
          <div className="flex justify-end w-full">
            <div className="max-w-[85%]">
              <UserMessageDetails sendConfig={message.sendConfig} prodMode={prodMode} />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative py-4" data-scroll-container="chat">
      {messages.length === 0 ? (
        <div className="flex flex-col items-center h-full text-center px-4 pt-[25vh]">
          <div className="mb-8 w-full max-w-3xl px-4">
            {/* Welcome/Icebreaker display with navigation */}
            <div className="flex items-center justify-center gap-3 mb-6">
              {/* Left navigation caret - show based on canGoPrevious (icebreakers don't wrap, welcomes/taglines do) */}
              {canGoPrevious ? (
                <button
                  onClick={goToPrevious}
                  className="p-1 rounded-full hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
                  title="Previous"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              ) : (
                <div className="w-8" /> /* Spacer to maintain layout */
              )}
              
              {/* Welcome/Icebreaker text with colorized ionoff using same AI-dependent logic as sidebar */}
              {/* Welcome/icebreaker message with colorized ionoff and newline support */}
              <div 
                className="text-3xl font-semibold text-center [&_a]:underline [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:hover:text-blue-800 dark:[&_a]:hover:text-blue-300 [&_a]:transition-colors" 
                style={{ fontFamily: "'Special Elite', serif" }}
              >
                {(() => {
                  // Parse <newline> tags to split into multiple lines
                  const lines = currentMessage.split(/<newline>/i);
                  
                  // Helper to render a single line with colorized ionoff
                  const renderLineWithIonoff = (line: string, lineIndex: number, isLastLine: boolean = false) => {
                    const lowerLine = line.toLowerCase();
                    const ionoffIndex = lowerLine.indexOf('ionoff');
                    
                    // Inline X dismiss button for icebreakers (only on last line)
                    const dismissButton = (isLastLine && displayMode === 'icebreaker') ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissCurrentIcebreaker();
                        }}
                        className="inline-flex items-center justify-center ml-2 w-6 h-6 rounded bg-gray-600/50 hover:bg-gray-600 text-gray-400 hover:text-white transition-all"
                        title="Dismiss this icebreaker"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null;
                    
                    if (ionoffIndex === -1) {
                      // No "ionoff" in this line, render as-is
                      return <span key={lineIndex}>{line}{dismissButton}</span>;
                    }
                    
                    const beforeIonoff = line.slice(0, ionoffIndex);
                    const afterIonoff = line.slice(ionoffIndex + 6); // 6 = 'ionoff'.length
                    
                    return (
                      <span key={lineIndex}>
                        {beforeIonoff}
                        {tagColors.length > 0 ? (
                          <MultiColorIonoff colors={tagColors} size="large" />
                        ) : (
                          // Fallback when no AI models selected: use default 3-segment coloring
                          <span className="inline-flex gap-1 items-center">
                            <span className="inline-flex items-center justify-center bg-black text-[#ffe119] rounded text-[1.875rem] font-bold" style={{ padding: '0.25rem 0.5rem', minWidth: '1.5rem' }}>i</span>
                            <span className="inline-flex items-center justify-center bg-black text-[#3cb44b] rounded text-[1.875rem] font-bold" style={{ padding: '0.25rem 0.5rem', minWidth: '1.5rem' }}>on</span>
                            <span className="inline-flex items-center justify-center bg-black text-[#fabed4] rounded text-[1.875rem] font-bold" style={{ padding: '0.25rem 0.5rem', minWidth: '1.5rem' }}>off</span>
                          </span>
                        )}
                        {afterIonoff}{dismissButton}
                      </span>
                    );
                  };
                  
                  // If single line, render normally
                  if (lines.length === 1) {
                    return renderLineWithIonoff(lines[0], 0, true);
                  }
                  
                  // Multiple lines - render each on its own centered line
                  return (
                    <div className="flex flex-col items-center gap-1">
                      {lines.map((line, index) => (
                        <div key={index}>
                          {renderLineWithIonoff(line.trim(), index, index === lines.length - 1)}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              {/* Right navigation caret - show based on canGoNext (icebreakers don't wrap, welcomes/taglines do) */}
              {canGoNext ? (
                <button
                  onClick={goToNext}
                  className="p-1 rounded-full hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
                  title="Next"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              ) : (
                <div className="w-8" /> /* Spacer to maintain layout */
              )}
            </div>
            
            {/* Chat input - hidden when bottom section is collapsed */}
            {!isBottomSectionCollapsed && (
              <div className="w-full">
                <ChatInput 
                  onSendMessage={onSendMessage}
                  disabled={disabled}
                  isInitialState={true}
                  placeholder={instructions.newConversation}
                  webEnabled={webEnabled}
                  setWebEnabled={setWebEnabled}
                  enterSendsEnabled={enterSendsEnabled}
                  setEnterSendsEnabled={setEnterSendsEnabled}
                  selfEvalEnabled={selfEvalEnabled}
                  setSelfEvalEnabled={setSelfEvalEnabled}
                  txtEnabled={txtEnabled}
                  setTxtEnabled={setTxtEnabled}
                  plusHumanEnabled={plusHumanEnabled}
                  setPlusHumanEnabled={setPlusHumanEnabled}
                  humanLeaderboardEnabled={humanLeaderboardEnabled}
                  setHumanLeaderboardEnabled={setHumanLeaderboardEnabled}
                  showAdminInput={showAdminInput}
                  setShowAdminInput={setShowAdminInput}
                  adminConversationCount={adminConversationCount}
                  setAdminConversationCount={setAdminConversationCount}
                  onLoadAdminConversations={onLoadAdminConversations}
                  isLoadingAdminConversations={isLoadingAdminConversations}
                  toolsExpanded={toolsExpanded}
                  setToolsExpanded={setToolsExpanded}
                  targetCount={targetCount}
                  prodMode={prodMode}
                  onHelpMeStart={toggleHelpMeStart}
                  isShowingIcebreakers={displayMode === 'icebreaker'}
                  currentIcebreaker={displayMode === 'icebreaker' ? currentMessage : undefined}
                  tagColors={tagColors}
                  onInputChange={setHasInputText}
                  reasoningEffort={reasoningEffort}
                  setReasoningEffort={setReasoningEffort}
                  postscriptIntro={instructions.helpmestartPostscriptIntro}
                  customreviewPostscriptIntro={instructions.customreviewPostscriptIntro}
                  activeRubric={activeRubric}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          {(() => {
            const gradeMatrixMessages = messages.filter(m => m.isGradeMatrix);
            if (gradeMatrixMessages.length > 0) {
              console.log('🎨 ChatWindow rendering - found grade matrix messages:', {
                count: gradeMatrixMessages.length,
                totalMessages: messages.length,
                ids: gradeMatrixMessages.map(m => m.id)
              });
            }
            return null;
          })()}
          {(() => {
            // Filter messages based on clean view mode
            // Pure whitelist approach: only show explicitly allowed message types
            // 
            // 🔒 SECURITY FIX: In prodMode, ALWAYS filter regardless of cleanViewEnabled toggle
            // This prevents debug messages from leaking to normal users even if there's a race condition
            // The devModeOverride easter egg sets prodMode=false, so developers can still see debug messages
            const shouldFilter = prodMode || cleanViewEnabled;
            
            const filteredMessages = shouldFilter 
              ? orderedMessages.filter(message => {
                  // Show user messages
                  if (message.role === 'user') return true;
                  
                  // Show AI model responses (backend explicitly marks these with isPromptResponse: true)
                  if (message.isPromptResponse === true) return true;
                  
                  // Show ionoff synthesis (final response, not variants)
                  if (message.isSynthesized === true && !message.synthesisVariant) return true;
                  
                  // Hide everything else
                  return false;
                })
              : orderedMessages;
            
            return filteredMessages.map((message, filteredIndex) => {
            // Find the original index of this message in the unfiltered array
            const messageIndex = messages.findIndex(m => m.id === message.id);
            
            // Debug: Log grade matrix messages during render
            if (message.isGradeMatrix) {
              console.log('🎨 Rendering grade matrix message:', {
                id: message.id,
                index: messageIndex,
                contentPreview: message.content.substring(0, 50)
              });
            }
            
            // Handle debug info messages separately with compact component
            if (message.isDebugInfo) {
              return (
                <div key={message.id} className="py-3 px-4 md:px-6">
                  <DebugInfoMessage message={message} />
                </div>
              );
            }

            const isCompact = shouldBeCompact(message);
            const showLogButton = hasPreviousUserMessage(messageIndex);
            const canCollapse = isCollapsibleResponse(message);
            // First response for a prompt: default expanded, others: default collapsed
            // collapsedResponses tracks user toggles that override the default
            const isFirstResponse = canCollapse && isFirstResponseForPrompt(message, messageIndex);
            const defaultCollapsed = canCollapse && !isFirstResponse;
            // If user has toggled, invert the default; otherwise use default
            const userToggled = collapsedResponses.has(message.id);
            const isCollapsed = canCollapse && (defaultCollapsed ? !userToggled : userToggled);
            
            // 🎯 KEY FIX: Include synthesizing state in key to force re-render when it changes
            // This ensures ionoff messages properly transition from "synthesizing..." to final content
            const messageKey = message.isSynthesized 
              ? `${message.id}-synth-${message.synthesizing ? 'loading' : 'done'}`
              : message.id;
            
            return (
          <div
            key={messageKey}
            id={`message-${message.id}`}
            className={`relative py-6 px-4 md:px-6 border-b border-border transition-all duration-300 ${
              message.modelUsed === 'ionoff-error'
                ? 'bg-red-50/50 dark:bg-red-950/20 border-l-4 border-l-red-500'
                : message.isSynthesized && !message.synthesisVariant
                  ? 'bg-muted/20 ring-2 ring-black dark:ring-white'
                : message.role === 'assistant' || message.isSynthesized
                  ? 'bg-muted/20'
                  : ''
            } ${
              highlightedMessageId === message.id ? 'ring-4 ring-primary/50 bg-primary/10' : ''
            }`}
          >
                {/* Collapse/Expand button for AI responses */}
                {canCollapse && (
                  <button
                    onClick={() => toggleResponseCollapse(message.id)}
                    className="absolute top-2 right-4 p-2 rounded hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground z-10"
                    title={isCollapsed ? "Expand response" : "Collapse response"}
                  >
                    {isCollapsed ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronUp className="h-5 w-5" />
                    )}
                  </button>
                )}
                {isCompact ? (
                  /* Compact format for intermediate messages */
                  <CompactMessage
                    title={getCompactTitle(message)}
                    subtitle={getCompactSubtitle(message)}
                    timestamp={formatTimestamp(message.timestamp)}
                    avatar={
                      <div className="w-0 h-0"></div>
                    }
                    showRawMarkup={showRawMarkup.has(message.id)}
                    onToggleMarkup={() => toggleMarkupView(message.id)}
                    content={message.content}
                    onCopy={() => handleCopyMessage(message.id, message.content)}
                    onLoadLog={showLogButton ? () => handleLoadLog(message.id) : undefined}
                    cost={message.isExtractionResponse ? '<$0.01' : undefined}
                    costTooltip={message.isExtractionResponse ? 'Google AI - covered by free credits' : undefined}
                  >
                    {renderCompactMessageContent(message, showRawMarkup.has(message.id))}
                  </CompactMessage>
                ) : (
              /* Full format for user messages, synthesis, cost, and grade matrix */
              <div className="flex gap-4 items-start w-full">
                    {/* Avatar - hidden for all messages */}
                    <div className="flex-shrink-0 w-0"></div>

                    {/* Message content */}
                <div className="flex-1 min-w-0 relative w-full">
                      {/* Header: Role and Model */}
                      {message.role !== 'user' && (
                      <div className="flex items-baseline gap-2 mb-2">
                        <div className={`text-sm font-semibold ${
                          message.isHumanResponse ? '' :
                          message.modelUsed === 'ionoff-error' ? 'text-foreground/50' :
                          message.isCostSummary ? 'text-foreground/50' : 
                          message.isGradeMatrix ? 'text-foreground/50' : ''
                        }`}>
                          { 
                           message.isHumanResponse ? (
                             <span className="inline-flex items-center justify-center bg-black text-white px-2 py-1 rounded text-xs font-medium">
                               your response
                             </span>
                           ) :
                           message.modelUsed === 'ionoff-error' ? (
                             <span className="flex items-center gap-1">
                               API Error
                             </span>
                           ) :
                           message.isSynthesized ? (
                            (() => {
                              // For synthesis messages, compute tag colors from the prompt's sendConfig
                              const userMessage = messages.find(m => 
                                m.role === 'user' && 
                                m.timestamp.getTime() < message.timestamp.getTime() &&
                                m.sendConfig?.selectedModels
                              );
                              
                              // Use frozen color assignments from the user message if available
                              const frozenColors = (userMessage as any)?.colorAssignments 
                                ? new Map(Object.entries((userMessage as any).colorAssignments) as [string, string][])
                                : null;
                              const effectiveColors: Map<string, string> = frozenColors || modelColorAssignments || new Map();
                              
                              // Get chipAssignments for ModelChip lookup
                              const chipAssignments = (userMessage as any)?.chipAssignments as Array<{chipId: number; color: string; openrouterId: string; displayedModelID: string}> | undefined;
                              
                              const historicalModels = userMessage?.sendConfig?.selectedModels?.map((m: any) => m.openrouterId) || [];
                              const colors = highlighting.HIGHLIGHTER_COLORS;
                              const synthTagColors: string[] = historicalModels
                                .slice(0, Math.min(historicalModels.length, colors.length))
                                .map((modelId: string, index: number): string => {
                                  // Try to get color from frozen/effective assignments, fallback to highlighting colors
                                  return effectiveColors.get(modelId) || colors[index].hex;
                                });
                              
                              // Compute peer review champion from local grade state
                              // Filter grades to only those for THIS specific prompt
                              const promptId = message.userPromptMessageId || userMessage?.id;
                              let peerReviewChampion: { modelId: string; displayName: string; color: string } | null = null;
                              
                              if (localGradeState && promptId) {
                                // Filter peer grades (not self-eval) for this specific prompt
                                const promptPeerGrades = localGradeState.rawGrades.filter(
                                  (g: RawGrade) => !g.isSelfEval && g.userPromptMessageId === promptId
                                );
                                
                                if (promptPeerGrades.length > 0) {
                                  // Aggregate grades by candidate model
                                  const modelGradesMap = new Map<string, { grades: string[]; gradeCounts: GradeCounts }>();
                                  
                                  promptPeerGrades.forEach((g: RawGrade) => {
                                    const existing = modelGradesMap.get(g.candidateModelId);
                                    if (existing) {
                                      existing.grades.push(g.grade);
                                      existing.gradeCounts[g.grade as keyof GradeCounts]++;
                                    } else {
                                      const counts: GradeCounts = { A: 0, B: 0, C: 0, D: 0, F: 0 };
                                      counts[g.grade as keyof GradeCounts] = 1;
                                      modelGradesMap.set(g.candidateModelId, { grades: [g.grade], gradeCounts: counts });
                                    }
                                  });
                                  
                                  // Sort by majority judgment to find the winner
                                  const sortedModels = Array.from(modelGradesMap.entries())
                                    .map(([modelId, data]) => {
                                      // Majority grade from counts (single shared implementation)
                                      const totalGrades = Object.values(data.gradeCounts).reduce((a, b) => a + b, 0);
                                      const medianGrade = majorityGrade(data.gradeCounts) ?? 'C';
                                      
                                      return {
                                        modelId,
                                        majorityJudgment: {
                                          medianGrade,
                                          gradeCounts: data.gradeCounts,
                                          judgmentsNumber: totalGrades,
                                        } as MajorityJudgment
                                      };
                                    })
                                    .sort((a, b) => compareMajorityJudgments(a.majorityJudgment, b.majorityJudgment));
                                  
                                  if (sortedModels.length > 0) {
                                    const winnerModelId = sortedModels[0].modelId;
                                    
                                    // Find display name and color from chipAssignments
                                    const chipAssignment = chipAssignments?.find(c => 
                                      c.openrouterId === winnerModelId || c.displayedModelID === winnerModelId
                                    );
                                    
                                    // Get color from frozen colors or effective colors
                                    let winnerColor = effectiveColors.get(winnerModelId) || '#CCCCCC';
                                    if (chipAssignment) {
                                      winnerColor = chipAssignment.color;
                                    }
                                    
                                    peerReviewChampion = {
                                      modelId: winnerModelId,
                                      displayName: chipAssignment?.displayedModelID || winnerModelId,
                                      color: winnerColor
                                    };
                                  }
                                }
                              }
                              
                              console.log('🎨 Synthesis logo colors:', {
                                messageId: message.id.substring(0, 8),
                                isFrozen: !!frozenColors,
                                synthTagColors,
                                peerReviewChampion: peerReviewChampion?.modelId,
                              });
                              
                              // Use current showModelNames state for synchronized display across all chips
                              // (double-click on any chip toggles all chips)
                              
                              return (
                                <span className="inline-flex items-center gap-2">
                                  <MultiColorIonoff colors={synthTagColors} className="inline-flex gap-0.5" />
                                  {peerReviewChampion && (
                                    <>
                                      <span className="text-xs text-muted-foreground font-normal">synthesizer:</span>
                                      <ModelChip
                                        modelId={peerReviewChampion.displayName}
                                        isActive={false}
                                        backgroundColor={peerReviewChampion.color}
                                        onClick={() => {}}
                                        showModelNames={showModelNames}
                                        onDoubleClick={onToggleModelNames}
                                      />
                                    </>
                                  )}
                                </span>
                              );
                            })()
                          ) :
                           message.isCostSummary ? (
                             <span className="text-foreground/50">Cost Summary</span>
                           ) :
                           message.isGradeMatrix ? (
                             <span className="text-foreground/50">Grade Matrix</span>
                           ) :
                           message.isGradingResponse ? (
                             <span className="text-foreground/50">{`${message.modelUsed || 'AI'} (Grading)`}</span>
                           ) :
                          // Show ModelChip for any assistant message with a modelUsed
                          message.modelUsed ? (
                            (() => {
                              // Find the user prompt message for this response to get frozen visual state
                              const userMessage = messages.slice(0, messageIndex).reverse().find(m => m.role === 'user');
                              
                              // NEW: Chip-ID based lookup (canonical)
                              const chipAssignments = (userMessage as any)?.chipAssignments as Array<{chipId: number; color: string; openrouterId: string; displayedModelID: string}> | undefined;
                              const messageChipId = (message as any)?.chipId as number | null | undefined;
                              
                              // LEGACY: Color map based lookup (fallback for old messages)
                              const frozenColors = (userMessage as any)?.colorAssignments 
                                ? new Map(Object.entries((userMessage as any).colorAssignments) as [string, string][])
                                : null;
                              
                              // Use current showModelNames state for synchronized display across all chips
                              // (double-click on any chip toggles all chips)
                              
                              // Determine color by matching model ID to chipAssignments
                              let assignedColor: string = '#CCCCCC';
                              let hasColor = false;
                              
                              if (chipAssignments) {
                                // Try to find color by matching actualModelId (openrouterId) or modelUsed (displayedModelID)
                                const chipAssignment = chipAssignments.find(c => {
                                  // First try exact match on actualModelId (openrouterId)
                                  if (message.actualModelId && c.openrouterId === message.actualModelId) {
                                    return true;
                                  }
                                  // Then try exact match on modelUsed (displayedModelID)
                                  if (message.modelUsed && c.displayedModelID === message.modelUsed) {
                                    return true;
                                  }
                                  // Fallback: strip reasoning suffix and try again
                                  const cleanModelUsed = message.modelUsed?.replace(/\s*\((high|low|medium|default|reasoning)\)\s*$/, '');
                                  if (cleanModelUsed && c.displayedModelID === cleanModelUsed) {
                                    return true;
                                  }
                                  return false;
                                });
                                
                                if (chipAssignment) {
                                  assignedColor = chipAssignment.color;
                                  hasColor = true;
                                }
                              } else if (frozenColors) {
                                // LEGACY PATH: Fall back to colorAssignments map for old messages
                                const cleanModelId = message.modelUsed!.replace(/\s*\((high|low|medium|default|reasoning)\)\s*$/, '');
                                assignedColor = frozenColors.get(cleanModelId) || '#CCCCCC';
                                hasColor = frozenColors.has(cleanModelId);
                              }
                              
                              console.log('🎨 Message title visual state:', {
                                messageId: message.id.substring(0, 8),
                                modelUsed: message.modelUsed,
                                chipId: messageChipId,
                                hasChipAssignments: !!chipAssignments,
                                isFrozenColor: !!frozenColors,
                                showModelNames,
                                hasColor,
                                color: assignedColor,
                              });
                              
                              if (!hasColor) {
                                // No color assignment found, just show plain text
                                return message.modelUsed || 'AI';
                              }
                              
                              // Render prefix separately in gray, then ModelChip with color
                              // Display the modelUsed as-is (should be displayedModelID from backend)
                              const prefix = prioritySetting ? `/ionoff/${prioritySetting}/` : '/ionoff/';
                              return (
                                <span className="inline-flex items-center gap-0">
                                  <span className="text-xs text-muted-foreground font-normal">
                                    {prefix}
                                  </span>
                                  <ModelChip
                                    modelId={message.modelUsed!}
                                    isActive={false} // TODO: Implement chip-ID based highlighting
                                    backgroundColor={assignedColor}
                                    onClick={() => {}} // TODO: Implement chip-ID based highlighting
                                    showModelNames={showModelNames}
                                    showFullId={showModelNames}
                                    onDoubleClick={onToggleModelNames}
                                  />
                                </span>
                              );
                            })()
                          ) :
                             message.modelUsed || 'AI'}
                        </div>
                        {message.isCostSummary && (
                          <div className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 font-medium">
                            cost summary
                          </div>
                        )}
                        {message.isGradeMatrix && (
                          <div className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium">
                            grade matrix
                          </div>
                        )}
                      </div>
                      )}

                      {/* Collapsible content - hidden when collapsed */}
                      {!isCollapsed && (
                        <>
                          {/* Subtitle for grading/extraction responses */}
                          {(message.isGradingResponse || message.isExtractionResponse) && (
                            <div className="text-xs text-muted-foreground mb-3">
                              {message.isGradingResponse && message.candidateMapping && (
                                <>
                                  → Evaluated Models {message.candidateMapping.map((mapping, i) => 
                                    `#${mapping.modelIdx}=${mapping.modelId}=Candidate ${mapping.candidateNum}`
                                  ).join(', ')}
                                </>
                              )}
                              {message.isExtractionResponse && message.extractedFrom !== undefined && (
                                <>
                                  → Extracted grades from Model #{message.extractedFrom}{message.extractedFromModelId ? `=${message.extractedFromModelId}` : ''}
                                </>
                              )}
                            </div>
                          )}

                          {/* Response Content */}
                          {renderFullMessageContent(message, showRawMarkup.has(message.id))}

                      {/* Timestamp with Copy, Log, Markup Toggle, Edit, Star Rating, Resume Button, and Synthesis Checkbox */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3 clear-both">
                        <span>{formatTimestamp(message.timestamp)}</span>
                        <button
                          onClick={() => handleCopyMessage(message.id, message.content)}
                          className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                          title="Copy message content"
                        >
                          {copiedMessages.has(message.id) ? (
                            <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                        {showLogButton && (
                          <button
                            onClick={() => handleLoadLog(message.id)}
                            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                            title="Load log between user prompt and this message to clipboard"
                          >
                            <FileText className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => toggleMarkupView(message.id)}
                          className="flex items-center gap-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                          title={showRawMarkup.has(message.id) ? 'Switch to rendered view' : 'Switch to raw markup'}
                        >
                          {showRawMarkup.has(message.id) ? (
                            <>
                              <span className="text-xs">markup</span>
                              <ArrowUp className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              <span className="text-xs">markdown</span>
                              <ArrowDown className="w-3 h-3" />
                            </>
                          )}
                        </button>
                        
                        {/* Cost Display - for assistant messages */}
                        {/* Direct Google API: show "<$0.01" (covered by free credits) */}
                        {/* OpenRouter: show actual cost or "<$0.01" for tiny costs */}
                        {/* For ionoff synthesis: only show once costCalculated is true */}
                        {message.role === 'assistant' && 
                         !message.isSynthesized &&
                         !message.isCostSummary &&
                         !message.isGradeMatrix &&
                         message.debug?.provider === 'Gemini' && (
                          <span 
                            className="text-xs text-muted-foreground font-mono"
                            title="Google AI - covered by free credits"
                          >
                            {'<$0.01'}
                          </span>
                        )}
                        {message.role === 'assistant' && 
                         message.costUSD !== undefined && 
                         message.costUSD > 0 && 
                         message.debug?.provider !== 'Gemini' &&
                         // For ionoff synthesis, only show once calculated (not the initial header estimate)
                         (message.isSynthesized ? message.costCalculated === true : true) && (
                          <span 
                            className="text-xs text-muted-foreground font-mono"
                            title={message.isSynthesized 
                              ? `Total ionoff cost (${message.costMethod || 'calculated'}): $${message.costUSD.toFixed(4)}`
                              : `API cost: $${message.costUSD.toFixed(4)}`
                            }
                          >
                            {Math.round(message.costUSD * 100) > 0 
                              ? `$${(Math.round(message.costUSD * 100) / 100).toFixed(2)}`
                              : '<$0.01'
                            }
                          </span>
                        )}
                        
                        {/* 🎯 PHASE 2: Edit Button - for editable user prompts */}
                        {message.role === 'user' && !message.overwritten && onEditPrompt && (() => {
                          // Check if there's a next user prompt after this one (not overwritten)
                          const userPrompts = messages
                            .filter(m => m.role === 'user' && !m.overwritten)
                            .sort((a, b) => (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0));
                          const promptIndex = userPrompts.findIndex(m => m.id === message.id);
                          const hasNextPrompt = promptIndex >= 0 && promptIndex < userPrompts.length - 1;
                          
                          // If there's a next prompt, this prompt is never editable
                          if (hasNextPrompt) return null;
                          
                          // If stopped before EOP or reached EOP, it's editable
                          const isEditable = message.stoppedBeforeEOP || message.eop;
                          
                          return isEditable ? (
                            <EditPromptButton
                              onEdit={() => onEditPrompt(message.id, message.content)}
                            />
                          ) : null;
                        })()}
                        
                        {/* Prompt Analysis Info - for user prompts with calculated values */}
                        {message.role === 'user' && (message.shannonSurpriseBits !== undefined || (message as any).promptReadingLevel) && (
                          <div className="flex items-center gap-2 ml-auto">
                            {expandedPromptMeta.has(message.id) ? (
                              <>
                                <span 
                                  className="text-muted-foreground font-mono text-xs"
                                  title="Message ID (click to copy)"
                                  onClick={() => {
                                    navigator.clipboard.writeText(message.id);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {message.id}
                                </span>
                                {(message as any).shannonTokenCount !== undefined && (
                                  <span 
                                    className="text-muted-foreground"
                                    title="Number of tokens analyzed by davinci-002"
                                  >
                                    tokens {(message as any).shannonTokenCount}
                                  </span>
                                )}
                                {(message as any).shannonPromptSizeBits !== undefined && (
                                  <span 
                                    className="text-muted-foreground"
                                    title="Size to store prompt as text file (UTF-8 bytes × 8)"
                                  >
                                    size {(message as any).shannonPromptSizeBits}
                                  </span>
                                )}
                                {message.shannonSurpriseBits !== undefined && (
                                  <span 
                                    className="text-muted-foreground"
                                    title="Shannon surprise: cumulative information content in bits"
                                  >
                                    sprise {message.shannonSurpriseBits}
                                  </span>
                                )}
                                {message.shannonSurpriseBits !== undefined && (message as any).shannonTokenCount > 0 && (
                                  <span 
                                    className="text-muted-foreground"
                                    title="Perplexity: surprise per token (bits/token)"
                                  >
                                    plexity {Math.round(message.shannonSurpriseBits / (message as any).shannonTokenCount)}
                                  </span>
                                )}
                                {(message as any).promptCommunicationType && (
                                  <span 
                                    className="text-muted-foreground"
                                    title="Communication type, reading level, and count (sentences/lines/words)"
                                  >
                                    {(message as any).promptCommunicationType} {(message as any).promptReadingLevel || '?'}{(message as any).promptTypeCount !== undefined ? ` (${(message as any).promptTypeCount})` : ''}
                                  </span>
                                )}
                              </>
                            ) : null}
                            <button
                              onClick={() => {
                                setExpandedPromptMeta(prev => {
                                  const next = new Set(prev);
                                  if (next.has(message.id)) {
                                    next.delete(message.id);
                                  } else {
                                    next.add(message.id);
                                  }
                                  return next;
                                });
                              }}
                              className="text-muted-foreground hover:text-foreground transition-colors"
                              title={expandedPromptMeta.has(message.id) ? "Hide prompt metadata" : "Show prompt metadata (tokens, size, surprise, etc.)"}
                            >
                              <MoreHorizontal className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        
                        {/* Star Rating Control - for non-human assistant responses */}
                        {message.role === 'assistant' && 
                         !message.isHumanResponse && 
                         !message.isGradingResponse && 
                         !message.isCostSummary && 
                         !message.isGradeMatrix &&
                         !message.isDebugInfo &&
                         message.modelUsed !== 'ionoff-error' && (
                          <StarRatingControl
                            messageId={message.id}
                            currentGrade={message.humanGrade?.grade}
                            onRate={(grade) => {
                              if (!assignStarGrade) return;
                              const isSelfEval = message.isSynthesized || false;
                              const candidateModelId = message.actualModelId || message.modelUsed || '';
                              assignStarGrade(message.id, grade, candidateModelId, isSelfEval);
                            }}
                            onDelete={() => {
                              if (!deleteStarGrade) return;
                              deleteStarGrade(message.id);
                            }}
                            disabled={disabled}
                          />
                        )}
                        
                        {/* Resume Button - for TxT pause flow */}
                        {txtPaused && message.id === txtResumeMessageId && resumeFromPause && (
                          <ResumeButton
                            onResume={resumeFromPause}
                            disabled={!canResume || isResuming}
                            isResuming={isResuming}
                            phase={txtPausePhase || 'awaiting-model-grades'}
                            gradesReceived={
                              txtPausePhase === 'awaiting-ionoff-grade' 
                                ? (txtIonoffGradeReceived || 0)
                                : (txtModelGradesReceived || 0)
                            }
                            gradesRequired={
                              txtPausePhase === 'awaiting-ionoff-grade' ? 1 : 2
                            }
                          />
                        )}
                      </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          });
          })()}
        </div>
      )}
    </div>
  );
};
